import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Svg from '../../Svg/Svg';
import { useTranslation } from 'react-i18next';

//mui
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import AlbumOutlinedIcon from '@mui/icons-material/AlbumOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import HistoryIcon from '@mui/icons-material/History';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Fade from '@mui/material/Fade';

import WorklistContextSubmenu from './WorklistContextSubmenu';
import { MemoizedInlineEditCell as InlineEditCell } from '@rs-ui/components/Worklist/WorklistGrid/cells/InlineEditCell';
import fhirExtensionUrls from '@rs-core/fhir/extension/fhirExtensionUrls';
import getUserFullName from '@rs-core/fhir/resource/columnMapping/utils/getUserFullName';

import Toast from '@rs-ui/components/Toast/Toast';
import { useSelection } from '@rs-core/context/SelectionContext';
import useToast from '@rs-core/hooks/useToast';
import { useConfig } from '@rs-core/context/ConfigContext';
import useFhirDataLoader from '@worklist-2/core/src/hooks/useFhirDataLoader';
import { searchScopes } from '@rs-core/context/consts/searchScopes';
import { useAuth } from '@rs-core/context/UserAuthContext';
import findFinalDocuments from '@rs-ui/views/ImageViewerView3D/api/findFinalDocuments';
import extensionUrls from '@worklist-2/core/src/fhir/extension/fhirExtensionUrls';
import { DownloadDiscImageBurner } from '@rs-ui/views/utils/downloader';

const getOrganizationId = resource => resource?.imagingOrganizationId || resource?.internalManagingOrganizationID;
const getStudyStatus = resource =>
	resource?.extension?.find(ext => ext.url == fhirExtensionUrls.common.studyStatus)?.valueCoding?.display;

const WorklistContextMenu = ({
	wheelMenuItems = [],
	setPoints,
	points,
	onWheelMenuItemClick,
	onRemoveStudiesFromTF,
	rightClickedItem,
	onContextMenuClose,
	updateData,
}) => {
	const { t } = useTranslation('worklistContextMenu');
	const [contextMenuTopItems, setContextWheelMenuItems] = useState([]);
	const [transformOrigin, setTransformOrigin] = useState({
		vertical: 'top',
		horizontal: 'left',
	});

	const [enableAssignPhysicians, setEnableAssignPhysicians] = useState(true);

	const { hasPermissionByOrganizationAsync } = useAuth();
	const { setToastUtility, toastOpen, toastMessage, handleToastClose } = useToast();

	// Toast message display duration
	const [duration, setDuration] = useState(null);
	const { selectedStudies, resetStudiesSelection, toggleStudyPresence } = useSelection();
	// delete study
	const [enableDeleteStudies, setEnableDeleteStudies] = useState(true);
	const studyLoader = useFhirDataLoader({
		scope: searchScopes.studyRegular,
	});

	useEffect(() => {
		const uniqueInternalManagingOrganizationIds = new Set(
			selectedStudies?.map(ss => ss.internalManagingOrganizationId)
		).size;

		setEnableAssignPhysicians(uniqueInternalManagingOrganizationIds <= 1);
	}, [selectedStudies]);

	// check if we click outside of selected studes
	useEffect(() => {
		if (selectedStudies?.length > 0 && rightClickedItem) {
			const clickedStudyInsideSelectedStudies = selectedStudies.some(study => study.id === rightClickedItem.id);

			if (!clickedStudyInsideSelectedStudies) {
				resetStudiesSelection();
			}
		} else if (rightClickedItem?.id > 0) {
			toggleStudyPresence({ id: rightClickedItem.id, data: rightClickedItem });
		}
	}, [selectedStudies, rightClickedItem, toggleStudyPresence]);

	const __config = useConfig();

	/**
	 * showErrorDownloadMsg
	 */
	const showErrorDownloadMsg = label => {
		setDuration(5000);
		setToastUtility(true, `${t(label || 'No images available for download')}`);
	};

	const isTFScreenOpen = useMemo(() => !!onRemoveStudiesFromTF, [onRemoveStudiesFromTF]);

	useEffect(() => {
		const checkPermissions = async () => {
			if (isTFScreenOpen) {
				setEnableDeleteStudies(false);
				return;
			}

			const studiesManagingOrgsArray = selectedStudies?.map(
				s => s.internalManagingOrganizationID ?? s.internalManagingOrganizationId
			);

			const studiesManagingOrgsSet = new Set(studiesManagingOrgsArray);
			const deleteStudyPermissionPath = 'worklist.delete study';

			const hasAllPermissions = await Promise.all(
				Array.from(studiesManagingOrgsSet).map(internalManagingOrganizationId =>
					hasPermissionByOrganizationAsync(internalManagingOrganizationId, 'Home', deleteStudyPermissionPath)
				)
			);

			setEnableDeleteStudies(hasAllPermissions.every(Boolean));
		};

		checkPermissions();
	}, [selectedStudies, isTFScreenOpen, rightClickedItem]);

	const getClickType = (RightClickeddata, Selecteddata) => {
		const getDicomUid = item => {
			if (item.dicomUid) return item.dicomUid;
			return item.identifier?.find(id => id.system === 'urn:dicom:uid')?.value;
		};

		if (RightClickeddata.length !== Selecteddata.length) {
			return false;
		}

		const isSame = RightClickeddata.every((item, index) => {
			const rightUid = getDicomUid(item);
			const selectedUid = getDicomUid(Selecteddata[index]);
			return rightUid === selectedUid;
		});

		return !!isSame;
	};

	const onBurnToDiscClick = async () => {
		const data = selectedStudies;

		const isUserRightClicked = getClickType([rightClickedItem], selectedStudies);

		if (!data || data.length === 0) return;

		setDuration(null);
		const studyIds = isUserRightClicked
			? data.map(item => item.identifier.find(id => id.system === 'urn:dicom:uid')?.value?.split('urn:oid:')?.[1])
			: data?.map(({ dicomUid }) => dicomUid);
		const hasDuplicates = new Set(studyIds).size !== studyIds.length;
		if (hasDuplicates) {
			setToastUtility(true, t(`Cannot download same studyUID`, { ns: 'downloader' }));
			return false;
		}

		const patientsInfo = [...new Set(data?.map(({ subject }) => subject.id))];
		const internalManagingOrganizationIds = isUserRightClicked
			? data.map(item => item.internalManagingOrganizationID)
			: data.map(({ internalManagingOrganizationId }) => internalManagingOrganizationId);

		await findFinalDocuments({ __config, patientsInfo, refData: data });

		const atLeastOneImageOrFinalReport = data.every(
			item => item.rawData.numImages > 0 || item.rawData.numFinalReports > 0
		);

		if (!atLeastOneImageOrFinalReport) {
			showErrorDownloadMsg('selectedEmptyOrder');
		} else {
			const accessionNum = data
				.map(
					item =>
						item.rawData?.identifier?.find(elem => elem.system == extensionUrls.imagingStudy.accession)
							?.value
				)
				.filter((value, index, self) => self.indexOf(value) === index)
				.join('-')
				.substring(0, 200); // Windows max filename is 260, so let's save some space for the prefix and sulfix when available

			setDuration(5000);
			const download = new DownloadDiscImageBurner({
				uniqueId: studyIds.join(', '),
				authUrl: __config.auth.omegaai.auth_api,
				accessionNumber: accessionNum,
				studyIds,
				internalManagingOrganizationIds,
			}).start({
				errorHandler: () => {
					showErrorDownloadMsg('CD Burner Error on Create');
				},
			});

			if (download.enqueueStatus === 'ENQUEUED') {
				setToastUtility(true, t(`prepareToBurnDisc`, { ns: 'downloader' }));
			} else if (download.enqueueStatus === 'ALREADY-ENQUEUED') {
				setToastUtility(true, t(`This is being downloaded already.`, { ns: 'downloader' }));
			}
		}
		resetStudiesSelection();
	};

	const onDeleteClick = async () => {
		const data = selectedStudies;
		if (!data || data.length === 0) return;

		setToastUtility(true, `${t('Deleting')} ${data.length} ${t('studies')}...`);

		try {
			// Promise.all for concurrent deletions and avoids unnecessary Promise.resolve
			await Promise.all(data.map(s => studyLoader.delete(s.id)));
			setToastUtility(true, t('Successfully deleted selected studies.'));
			resetStudiesSelection();
		} catch (error) {
			console.error('Error deleting studies:', error);
			setToastUtility(true, t('Failed to delete selected studies.'));
		}
	};

	const contextMenuItems = useCallback(
		() => [
			{
				value: t('Performing Physician'),
				menuLevel: 0,
				icon: (
					<Svg
						data-testid="wowrklist-menu-perf-phy"
						name="WorklistMenuPerfPhy"
						sx={{ transform: 'scale(0.85)' }}
					/>
				),
				nestedOptions: [
					{
						value: 'performingPhysician',
						menuLevel: 1,
						cell: () => (
							<InlineEditCell
								checkStudyReadOnly
								openCard
								renderFromMenu
								cellLabel="performingPhysician"
								columnSize={265}
								internalStudyId={rightClickedItem.id}
								label="performing physician"
								organizationId={getOrganizationId(rightClickedItem)}
								searchParams={{
									ispractitionerroleactive: true,
									managingorganization: rightClickedItem?.internalManagingOrganizationID,
									isperformingphysician: true,
								}}
								studyStatus={getStudyStatus(rightClickedItem)}
								userName={getUserFullName(rightClickedItem.performingPhysician, true)}
								onUpdate={performingPhysicianName => {
									onCloseMenu();
									updateData(
										rightClickedItem.index,
										'performingPhysician',
										performingPhysicianName,
										null,
										rightClickedItem.id
									);
								}}
							/>
						),
					},
				],
				visible: enableAssignPhysicians,
			},
			{
				value: t('Performing Technologist'),
				menuLevel: 0,
				icon: (
					<Svg
						data-testid="wowrklist-menu-perf-tec"
						name="WorklistMenuPerfTec"
						sx={{ transform: 'scale(0.85)' }}
					/>
				),
				nestedOptions: [
					{
						value: 'performingTechnologist',
						menuLevel: 1,
						cell: () => (
							<InlineEditCell
								checkStudyReadOnly
								openCard
								renderFromMenu
								cellLabel="performingTechnologist"
								columnSize={265}
								internalStudyId={rightClickedItem.id}
								label="performing technologist"
								organizationId={getOrganizationId(rightClickedItem)}
								searchParams={{
									ispractitionerroleactive: true,
									managingorganization: rightClickedItem?.internalManagingOrganizationID,
									isperformingtechnologist: true,
								}}
								studyStatus={getStudyStatus(rightClickedItem)}
								userName={getUserFullName(rightClickedItem.performingTechnologist, true)}
								onUpdate={performingTechnologistName => {
									onCloseMenu();
									updateData(
										rightClickedItem.index,
										'performingTechnologist',
										performingTechnologistName,
										null,
										rightClickedItem.id
									);
								}}
							/>
						),
					},
				],
				visible: enableAssignPhysicians,
			},
			{
				value: t('Reading Physician'),
				menuLevel: 0,
				icon: (
					<Svg
						data-testid="wowrklist-menu-read-phy"
						name="WorklistMenuReadPhy"
						sx={{ transform: 'scale(0.8)' }}
					/>
				),
				nestedOptions: [
					{
						value: 'readingPhysician',
						menuLevel: 1,
						cell: () => (
							<InlineEditCell
								checkStudyReadOnly
								openCard
								renderFromMenu
								cellLabel="readingPhysician"
								columnSize={265}
								internalStudyId={rightClickedItem.id}
								label="reading physician"
								organizationId={getOrganizationId(rightClickedItem)}
								searchParams={{
									ispractitionerroleactive: true,
									managingorganization: rightClickedItem?.internalManagingOrganizationID,
									isreadingphysician: true,
								}}
								studyStatus={getStudyStatus(rightClickedItem)}
								userName={getUserFullName(rightClickedItem.readingPhysician, true)}
								onUpdate={readingPhysicianName => {
									onCloseMenu();
									updateData(
										rightClickedItem.index,
										'readingPhysician',
										readingPhysicianName,
										null,
										rightClickedItem.id
									);
								}}
							/>
						),
					},
				],
				visible: enableAssignPhysicians,
			},
			{
				value: t('Referring Physician'),
				menuLevel: 0,
				icon: (
					<Svg
						data-testid="wowrklist-menu-ref-phy"
						name="WorklistMenuRefPhy"
						sx={{ transform: 'scale(0.8)' }}
					/>
				),
				nestedOptions: [
					{
						value: 'referringPhysician',
						menuLevel: 1,
						cell: () => (
							<InlineEditCell
								openCard
								renderFromMenu
								cellLabel="referringPhysician"
								columnSize={265}
								internalOrderId={rightClickedItem?.basedOn?.[0]?.id}
								internalStudyId={rightClickedItem.id}
								label="referring physician"
								referringPhysicianId={rightClickedItem?.referringPhysicianId}
								searchParams={{
									ispractitionerroleactive: true,
									managingorganization: rightClickedItem?.internalManagingOrganizationID,
									isreferringphysician: true,
									_elements: 'name',
									active: true,
								}}
								userName={getUserFullName(rightClickedItem.referringPhysician, true)}
								onUpdate={response => {
									onCloseMenu();
									updateData(
										rightClickedItem.index,
										'referringPhysician',
										response?.mavenReferringOrganizationNotUpdating
											? response?.requester?.display
											: response,
										null,
										rightClickedItem?.id
									);
									if (response?.mavenReferringOrganizationNotUpdating) {
										updateData(
											rightClickedItem.index,
											'referringFacility',
											response?.requester?.extension[0]?.valueReference?.display,
											null,
											rightClickedItem?.id
										);
									}
								}}
							/>
						),
					},
				],
				visible: enableAssignPhysicians,
			},
			{
				value: t('Transcriptionist'),
				menuLevel: 0,
				icon: (
					<Svg
						data-testid="wowrklist-menu-transcript"
						name="WorklistMenuTranscript"
						sx={{ transform: 'scale(0.8)' }}
					/>
				),
				nestedOptions: [
					{
						value: 'transcriptionist',
						menuLevel: 1,
						cell: () => (
							<InlineEditCell
								checkStudyReadOnly
								openCard
								renderFromMenu
								cellLabel="transcriptionist"
								columnSize={265}
								internalStudyId={rightClickedItem.id}
								label="transcriptionist"
								organizationId={getOrganizationId(rightClickedItem)}
								searchParams={{
									ispractitionerroleactive: true,
									managingorganization: rightClickedItem?.internalManagingOrganizationID,
									istranscriptionist: true,
								}}
								studyStatus={getStudyStatus(rightClickedItem)}
								userName={getUserFullName(rightClickedItem.transcriptionist, true)}
								onUpdate={transcriptionistName => {
									onCloseMenu();
									updateData(
										rightClickedItem.index,
										'transcriptionist',
										transcriptionistName,
										null,
										rightClickedItem.id
									);
								}}
							/>
						),
					},
				],
				visible: enableAssignPhysicians,
			},
			{
				value: 'divider',
				menuLevel: 0,
				visible: enableAssignPhysicians,
			},
			{
				value: t('Study Priority'),
				menuLevel: 0,
				icon: <SportsScoreIcon style={{ color: '#a4a4a4' }} />,
				nestedOptions: [
					{
						value: 'Placeholder',
						menuLevel: 1,
					},
				],
				visible: false, //turn on when it's implemented,
			},
			{
				value: t('Study Status'),
				menuLevel: 0,
				icon: <DescriptionOutlinedIcon style={{ color: '#a4a4a4' }} />,
				nestedOptions: [
					{
						value: 'Placeholder',
						menuLevel: 1,
					},
				],
				visible: false, //turn on when it's implemented,
			},
			{
				value: t('Audit Log'),
				menuLevel: 0,
				icon: <HistoryIcon style={{ color: '#a4a4a4' }} />,
				visible: false, //turn on when it's implemented,
			},
			{
				value: 'divider',
				menuLevel: 0,
				visible: false, //turn on when it's implemented,
			},
			{
				value: t('Download Study'),
				menuLevel: 0,
				icon: <DownloadIcon style={{ color: '#a4a4a4' }} />,
				nestedOptions: [
					{
						value: 'Download',
						menuLevel: 1,
					},
					{
						value: 'Anonymized Download',
						menuLevel: 1,
					},
				],
				visible: true,
			},
			{
				value: t('Burn Study'),
				menuLevel: 0,
				icon: <AlbumOutlinedIcon style={{ color: '#a4a4a4' }} />,
				visible: true,
			},
			{
				value: t('Post Charge'),
				menuLevel: 0,
				icon: <Svg data-testid="chargePost" name="ChargePost" sx={{ transform: 'scale(0.8)' }} />,
				visible: false, //turn on when it's implemented,
			},
			{
				value: t('Merge Patient'),
				menuLevel: 0,
				icon: <CallMergeIcon style={{ color: '#a4a4a4' }} />,
				visible: false, //turn on when it's implemented,
			},
			{
				value: t('Link Patient'),
				menuLevel: 0,
				icon: <InsertLinkIcon style={{ color: '#a4a4a4' }} />,
				visible: false, //turn on when it's implemented,
			},
			{
				value: t('Delete'),
				menuLevel: 0,
				icon: <DeleteIcon style={{ color: '#a4a4a4' }} />,
				visible: enableDeleteStudies,
			},
		],
		[enableAssignPhysicians, rightClickedItem, t, enableDeleteStudies]
	);

	useEffect(() => {
		if (points?.x !== 0 && points?.y !== 0) {
			const out = {};
			out.bottom = points.y + 520 > (window.innerHeight || document.documentElement.clientHeight);
			out.right = points.x + 300 > (window.innerWidth || document.documentElement.clientWidth);
			out.any = out.bottom || out.right;

			const newTransformOrigin = {
				vertical: 'top',
				horizontal: 'left',
			};

			if (out.bottom) {
				newTransformOrigin.vertical = 'bottom';
			}

			if (out.right) {
				newTransformOrigin.horizontal = 'right';
			}

			setTransformOrigin(newTransformOrigin);
		}
	}, [points]);

	useEffect(() => {
		if (wheelMenuItems.length > 0) {
			const items = wheelMenuItems
				.toSorted(({ worklistMenuOrder: a }, { worklistMenuOrder: b }) => a - b)
				.filter(item => item.icon !== 'order');
			setContextWheelMenuItems(items);
		} else {
			setContextWheelMenuItems([]);
		}
	}, [wheelMenuItems]);

	const onCloseMenu = () => {
		setPoints({ x: 0, y: 0 });
		onContextMenuClose();
	};

	const onOptionClick = option => {
		onCloseMenu();
		if (option.value === 'Burn Study') {
			onBurnToDiscClick();
		} else if (option.value === 'Delete') {
			onDeleteClick();
		}
	};

	return (
		<>
			<Menu
				transition
				TransitionComponent={Fade}
				anchorPosition={{ top: points.y, left: points.x }}
				anchorReference="anchorPosition"
				data-testid="worklist-context-menu"
				id="worklist-context-menu"
				open={points.x !== 0 && points.y !== 0}
				sx={{
					borderRadius: '10px',
					'& .MuiList-root': {
						background: '#1c1c1c',
						padding: '8px 0px 8px 0px',
					},
					'& .MuiDivider-root': {
						margin: 'unset !important',
					},
				}}
				transformOrigin={transformOrigin}
				onClose={() => onCloseMenu()}
				onContextMenu={e => {
					e.preventDefault();
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-around',
						backgroundColor: '#1c1c1c',
						marginTop: '0px',
					}}
				>
					{contextMenuTopItems.map((item, index) => (
						<Box key={`${index}menuitem`} onClick={() => onWheelMenuItemClick(item)}>
							<Tooltip placement="top-start" title={item.label}>
								<IconButton>
									<Svg
										name={item.worklistMenuIcon}
										sx={{ cursor: 'pointer', transform: 'scale(1.5)' }}
										viewBox="0 0 32 32"
									/>
								</IconButton>
							</Tooltip>
						</Box>
					))}
				</Box>
				<Box
					key={enableDeleteStudies + enableAssignPhysicians + JSON.stringify(points) + rightClickedItem?.id}
					sx={{ width: '300px' }}
				>
					<WorklistContextSubmenu
						contextMenuTopItems={contextMenuTopItems}
						menuLevels={2}
						options={contextMenuItems().filter(item => item.visible)}
						points={points}
						transformOrigin={transformOrigin}
						onOptionClick={onOptionClick}
						onWheelMenuItemClick={onWheelMenuItemClick}
					/>
				</Box>
			</Menu>
			<Toast
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				duration={duration}
				message={toastMessage}
				open={toastOpen}
				onClose={handleToastClose}
			/>
		</>
	);
};

export default WorklistContextMenu;
